import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, MapPin, Users, Plus } from "lucide-react";

function formatEventDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/events").then(({ data }) => {
      setEvents(data);
      setLoading(false);
    });
  }, []);

  const now = new Date();
  const upcoming = events.filter((e) => new Date(e.start_at) >= now);
  const past = events.filter((e) => new Date(e.start_at) < now);

  return (
    <div className="k-container py-12 md:py-16">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-display text-6xl text-ink-900" data-testid="events-title">
            Etkinlikler
          </h1>
          <p className="mt-2 font-body text-ink-700">Canlı atölyeler, sohbetler ve birlikte öğrenme.</p>
        </div>
        {user?.role === "admin" && (
          <Link to="/events-new" className="k-btn-primary" data-testid="new-event-btn">
            <Plus className="mr-2 h-4 w-4" /> Etkinlik oluştur
          </Link>
        )}
      </div>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-bold text-ink-900" data-testid="upcoming-heading">
          Yaklaşan
        </h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {loading && [1, 2].map((i) => <div key={i} className="k-card h-40 animate-pulse" />)}
          {!loading && upcoming.length === 0 && (
            <div className="k-card p-8 text-center font-body text-ink-500 md:col-span-2" data-testid="upcoming-empty">
              Yakında yeni bir buluşma duyuracağız.
            </div>
          )}
          {upcoming.map((e) => (
            <EventCard key={e.event_id} e={e} />
          ))}
        </div>
      </section>

      {past.length > 0 && (
        <section className="mt-14">
          <h2 className="font-heading text-2xl font-bold text-ink-900">Geçmiş</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 opacity-70">
            {past.map((e) => (
              <EventCard key={e.event_id} e={e} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function EventCard({ e }) {
  return (
    <Link
      to={`/events/${e.event_id}`}
      className="k-card p-6 transition-all hover:-translate-y-1 hover:shadow-warm"
      data-testid={`event-card-${e.event_id}`}
    >
      <div className="flex items-center gap-2 text-sm text-sage-700">
        <Calendar className="h-4 w-4" />
        <span className="font-body font-semibold">{formatEventDate(e.start_at)}</span>
      </div>
      <h3 className="mt-2 font-heading text-xl font-bold text-ink-900">{e.title}</h3>
      <p className="mt-2 line-clamp-2 font-body text-ink-700">{e.description}</p>
      <div className="mt-4 flex items-center justify-between text-sm text-ink-500">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-4 w-4" /> {e.location}
        </span>
        <span className="inline-flex items-center gap-1">
          <Users className="h-4 w-4" /> {e.rsvp_count}
          {e.capacity ? ` / ${e.capacity}` : ""}
        </span>
      </div>
    </Link>
  );
}
